import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  OutlinedInput,
  Chip,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import ClearIcon from '@mui/icons-material/Clear';
import { useLazyQuery, useQuery } from '@apollo/client';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from 'components/table/muiTablePaginationI18nProps';
import _ from 'lodash';
import {
  REPORT_STATISTICS_LIST_QUERY,
  REPORT_STATISTICS_COUNT_QUERY,
  REPORT_LIST_HOTEL_GROUPS_QUERY,
  REPORT_LIST_HOTELS_QUERY,
} from '../gql';
import {
  StatisticsReportFiltersInput,
  StatisticsReportOutput,
  ECountryCode,
  ContractType,
  EOfferSource,
} from '__generated__/graphql';
import { allCountriesSorted } from 'languages';
import { getSubdivisionsForCountry } from 'semshared/utils/subdivisions';
import { getContractTypeOptions } from './utils/contractHelpers';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import { formatNumber } from 'semshared/utils/format';
import StatisticsBookingsModal from './components/StatisticsBookingsModal';
import { StatisticsReportBookingsFiltersInput } from '__generated__/graphql';

const STATISTICS_RELEVANT_CONTRACT_TYPES = [
  'SYMPOSION_HOTEL_CONTRACT',
  'PARTNER_HOTEL_CONTRACT',
  'FRIEND_HOTEL_CONTRACT',
  'FRIEND_LOCATION_CONTRACT',
];

const getMonthName = (month: number) => {
  const date = new Date(2000, month - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, { month: 'long' }).format(date);
};

/** Calendar month of seminar start for this row (same definition as server); 0 = no date on latest version. */
const formatStatisticsBookingMonthCell = (row: StatisticsReportOutput): string => {
  if (row.eventYear === 0 || row.eventMonth === 0) {
    return String(i18next.t('statistics-no-seminar-start'));
  }
  const date = new Date(row.eventYear, row.eventMonth - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, { month: 'long' }).format(date);
};

type StatisticsSortField =
  | 'requestYear'
  | 'requestMonth'
  | 'eventPeriod'
  | 'hotelName'
  | 'offerSource'
  | 'totalRequests'
  | 'approvedCount'
  | 'rejectedByHotelCount'
  | 'rejectedByClientCount'
  | 'totalNights'
  | 'totalGuests';

type SortDirection = 'asc' | 'desc';

const compareStatisticsRows = (a: StatisticsReportOutput, b: StatisticsReportOutput, field: StatisticsSortField, direction: SortDirection): number => {
  const dir = direction === 'asc' ? 1 : -1;
  let primary = 0;
  switch (field) {
    case 'requestYear':
      primary = a.requestYear - b.requestYear;
      break;
    case 'requestMonth':
      primary = a.requestMonth - b.requestMonth;
      break;
    case 'eventPeriod': {
      const pa = a.eventYear * 100 + a.eventMonth;
      const pb = b.eventYear * 100 + b.eventMonth;
      primary = pa - pb;
      break;
    }
    case 'hotelName':
      primary = a.hotelName.localeCompare(b.hotelName, undefined, { sensitivity: 'base' });
      break;
    case 'offerSource':
      primary = String(a.offerSource).localeCompare(String(b.offerSource));
      break;
    case 'totalRequests':
      primary = a.totalRequests - b.totalRequests;
      break;
    case 'approvedCount':
      primary = a.approvedCount - b.approvedCount;
      break;
    case 'rejectedByHotelCount':
      primary = a.rejectedByHotelCount - b.rejectedByHotelCount;
      break;
    case 'rejectedByClientCount':
      primary = a.rejectedByClientCount - b.rejectedByClientCount;
      break;
    case 'totalNights':
      primary = a.totalNights - b.totalNights;
      break;
    case 'totalGuests':
      primary = a.totalGuests - b.totalGuests;
      break;
    default:
      primary = 0;
  }
  primary *= dir;
  if (primary !== 0) {
    return primary;
  }
  return (
    b.requestYear - a.requestYear ||
    b.requestMonth - a.requestMonth ||
    b.eventYear * 100 + b.eventMonth - (a.eventYear * 100 + a.eventMonth) ||
    a.hotelName.localeCompare(b.hotelName, undefined, { sensitivity: 'base' }) ||
    String(a.offerSource).localeCompare(String(b.offerSource))
  );
};

/**
 * Statistics report tab showing aggregated offer/booking statistics.
 * Supports filtering, pagination, and drill-down modal for booking details.
 */
export default function StatisticsTab() {
  // Local state instead of Redux
  const [filters, setFilters] = useState<StatisticsReportFiltersInput>({});
  const [results, setResults] = useState<StatisticsReportOutput[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 50,
    totalCount: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState<StatisticsReportBookingsFiltersInput | null>(null);
  const [modalColumnLabel, setModalColumnLabel] = useState('');
  const [allPagesTotals, setAllPagesTotals] = useState<{
    totalRequests: number;
    approvedCount: number;
    rejectedByHotelCount: number;
    rejectedByClientCount: number;
    totalNights: number;
    totalGuests: number;
  } | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const allResultsRef = useRef<StatisticsReportOutput[] | null>(null);
  const [statisticsSort, setStatisticsSort] = useState<{ field: StatisticsSortField; direction: SortDirection } | null>(null);

  // GraphQL queries
  const [executeStatisticsQuery] = useLazyQuery(REPORT_STATISTICS_LIST_QUERY);
  const [executeStatisticsCountQuery] = useLazyQuery(REPORT_STATISTICS_COUNT_QUERY);

  // Hotel groups query
  const { data: hotelGroupsData } = useQuery(REPORT_LIST_HOTEL_GROUPS_QUERY);
  const hotelGroups = hotelGroupsData?.reportListHotelGroups || [];

  // Hotel states from ISO 3166-2
  const hotelStates = React.useMemo(
    () => getSubdivisionsForCountry(filters.hotelCountry || ''),
    [filters.hotelCountry]
  );

  // Hotel autocomplete
  const [hotelSearchInput, setHotelSearchInput] = useState<string>('');
  const [executeHotelsQuery, { data: hotelsData, loading: hotelsLoading }] = useLazyQuery(REPORT_LIST_HOTELS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allHotels = hotelsData?.reportListHotels || [];

  // Sync hotelSearchInput with filters.hotelName on reset
  React.useEffect(() => {
    if (!filters.hotelName && hotelSearchInput) {
      // Only clear if filters were reset externally
      const hasAnyFilter = Object.keys(filters).length > 0;
      if (!hasAnyFilter) {
        setHotelSearchInput('');
      }
    }
  }, [filters]);

  // The hotel-name autocomplete only supports a single contract type, so only apply it
  // when exactly one specific type (not "ALL"/"NONE") is selected in the multiselect filter.
  const specificSelectedContractTypes = (filters.hotelContractTypes || []).filter((t) => t !== 'ALL' && t !== 'NONE');
  const autocompleteContractType = specificSelectedContractTypes.length === 1 ? (specificSelectedContractTypes[0] as ContractType) : undefined;

  // Trigger hotel search when input has at least 1 character
  React.useEffect(() => {
    const handler = setTimeout(() => {
      if (hotelSearchInput && hotelSearchInput.length >= 1) {
        executeHotelsQuery({
          variables: {
            country: filters.hotelCountry,
            state: filters.hotelState,
            groupIds: filters.hotelGroupIds,
            contractType: autocompleteContractType,
            name: hotelSearchInput,
            take: 20,
          },
        });
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [hotelSearchInput, filters.hotelCountry, filters.hotelState, filters.hotelGroupIds, autocompleteContractType, executeHotelsQuery]);

  // Filter change handler
  const handleFilterChange = (field: keyof StatisticsReportFiltersInput, value: any) => {
    setFilters((prev) => {
      if (value === undefined || value === null || value === '') {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      }
      return { ...prev, [field]: value };
    });
  };

  // Contract type options for the multiselect, including the "All" and "No Agreement" sentinel values
  const contractTypeFilterOptions = [
    { value: 'ALL', label: String(i18next.t('filter-status-all')) },
    { value: 'NONE', label: String(i18next.t('filter-agreement-no-contract')) },
    ...getContractTypeOptions().filter((option) => STATISTICS_RELEVANT_CONTRACT_TYPES.includes(option.value)),
  ];

  // "All" is a shortcut for selecting every relevant contract type, so picking it clears other
  // selections, and picking anything else while "All" is active drops "All" again.
  const handleContractTypesChange = (value: string[]) => {
    const prevSelection = filters.hotelContractTypes || [];
    let nextSelection = value;
    if (nextSelection.includes('ALL') && !prevSelection.includes('ALL')) {
      nextSelection = ['ALL'];
    } else if (nextSelection.length > 1 && nextSelection.includes('ALL')) {
      nextSelection = nextSelection.filter((v) => v !== 'ALL');
    }
    handleFilterChange('hotelContractTypes', nextSelection.length > 0 ? nextSelection : undefined);
  };

  // Reset filters
  const handleReset = () => {
    setFilters({});
    setResults([]);
    setStatisticsSort(null);
    setHotelSearchInput('');
    setPagination({
      page: 0,
      rowsPerPage: 50,
      totalCount: 0,
    });
  };

  // Compute totals from any list of statistics rows (used for all-pages totals)
  const computeTotalsFromList = useCallback((list: StatisticsReportOutput[]) => {
    if (list.length === 0) return null;
    return {
      totalRequests: list.reduce((sum, r) => sum + r.totalRequests, 0),
      approvedCount: list.reduce((sum, r) => sum + r.approvedCount, 0),
      rejectedByHotelCount: list.reduce((sum, r) => sum + r.rejectedByHotelCount, 0),
      rejectedByClientCount: list.reduce((sum, r) => sum + r.rejectedByClientCount, 0),
      totalNights: list.reduce((sum, r) => sum + r.totalNights, 0),
      totalGuests: list.reduce((sum, r) => sum + r.totalGuests, 0),
    };
  }, []);

  const sortedStatisticsRows = React.useMemo(() => {
    if (!statisticsSort) {
      return results;
    }
    const copy = [...results];
    copy.sort((a, b) => compareStatisticsRows(a, b, statisticsSort.field, statisticsSort.direction));
    return copy;
  }, [results, statisticsSort]);

  const handleStatisticsSort = (field: StatisticsSortField) => {
    setStatisticsSort((prev) => {
      if (prev?.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        field,
        direction: field === 'hotelName' || field === 'offerSource' || field === 'eventPeriod' ? 'asc' : 'desc',
      };
    });
  };

  // Generate statistics
  const handleGenerateStatistics = async (pageNumber = 0, pageSize = pagination.rowsPerPage) => {
    setLoading(true);
    setResults([]);
    setStatisticsSort(null);
    setAllPagesTotals(null);
    allResultsRef.current = null;

    try {
      const [statisticsResult, statisticsCountResult] = await Promise.all([
        executeStatisticsQuery({
          variables: {
            filters: filters,
            skip: pageNumber * pageSize,
            take: pageSize,
          },
          fetchPolicy: 'network-only',
        }),
        executeStatisticsCountQuery({
          variables: {
            filters: filters,
          },
          fetchPolicy: 'network-only',
        }),
      ]);

      const pageList = statisticsResult.data?.reportStatisticsList || [];
      const totalCount = statisticsCountResult.data?.reportStatisticsCount || 0;

      setResults(pageList);
      setPagination((prev) => ({
        ...prev,
        page: pageNumber,
        rowsPerPage: pageSize,
        totalCount,
      }));

      if (totalCount > 0) {
        if (totalCount <= pageSize) {
          const totals = computeTotalsFromList(pageList);
          setAllPagesTotals(totals);
          allResultsRef.current = pageList;
        } else {
          const allResult = await executeStatisticsQuery({
            variables: { filters, skip: 0, take: totalCount },
            fetchPolicy: 'network-only',
          });
          const allList = allResult.data?.reportStatisticsList || [];
          setAllPagesTotals(computeTotalsFromList(allList));
          allResultsRef.current = allList;
        }
      }
    } catch (error) {
      console.error('Statistics query error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    handleGenerateStatistics(newPage, pagination.rowsPerPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      rowsPerPage: newRowsPerPage,
    }));
    handleGenerateStatistics(0, newRowsPerPage);
  };

  // Group results by year, then by hotel
  const groupByYearAndHotel = (list: StatisticsReportOutput[]) => {
    const grouped: { [year: number]: { [hotelId: number]: { hotelName: string; results: StatisticsReportOutput[] } } } = {};

    list.forEach((result) => {
      if (!grouped[result.requestYear]) {
        grouped[result.requestYear] = {};
      }
      if (!grouped[result.requestYear][result.hotelId]) {
        grouped[result.requestYear][result.hotelId] = {
          hotelName: result.hotelName,
          results: [],
        };
      }
      grouped[result.requestYear][result.hotelId].results.push(result);
    });

    return grouped;
  };

  const calculateSubtotal = (subResults: StatisticsReportOutput[]) => {
    return {
      totalRequests: subResults.reduce((sum, r) => sum + r.totalRequests, 0),
      approvedCount: subResults.reduce((sum, r) => sum + r.approvedCount, 0),
      rejectedByHotelCount: subResults.reduce((sum, r) => sum + r.rejectedByHotelCount, 0),
      rejectedByClientCount: subResults.reduce((sum, r) => sum + r.rejectedByClientCount, 0),
      totalNights: subResults.reduce((sum, r) => sum + r.totalNights, 0),
      totalGuests: subResults.reduce((sum, r) => sum + r.totalGuests, 0),
    };
  };

  // CSV Export (all pages)
  const handleExportCSV = async () => {
    if (pagination.totalCount === 0) {
      alert(String(i18next.t('report-no-export-data')));
      return;
    }
    setExportLoading(true);
    try {
      let listToExport: StatisticsReportOutput[];
      if (allResultsRef.current && allResultsRef.current.length === pagination.totalCount) {
        listToExport = allResultsRef.current;
      } else {
        const allResult = await executeStatisticsQuery({
          variables: { filters, skip: 0, take: pagination.totalCount },
          fetchPolicy: 'network-only',
        });
        listToExport = allResult.data?.reportStatisticsList || [];
      }
      if (listToExport.length === 0) {
        alert(String(i18next.t('report-no-export-data')));
        return;
      }
      const headers = [
        String(i18next.t('statistics-year')),
        String(i18next.t('statistics-month')),
        String(i18next.t('statistics-booking-month-range')),
        String(i18next.t('statistics-hotel')),
        String(i18next.t('statistics-source')),
        String(i18next.t('statistics-requests')),
        String(i18next.t('statistics-reservations')),
        String(i18next.t('statistics-rejected-hotel')),
        String(i18next.t('statistics-rejected-client')),
        String(i18next.t('statistics-overnights')),
        String(i18next.t('statistics-participants')),
      ];
      const csvData = listToExport.map((r) => [
        r.requestYear,
        getMonthName(r.requestMonth),
        formatStatisticsBookingMonthCell(r),
        r.hotelName,
        r.offerSource === EOfferSource.LISTER ? String(i18next.t('enums-EOfferSource-LISTER')) : String(i18next.t('enums-EOfferSource-WIDGET')),
        formatNumber(r.totalRequests),
        formatNumber(r.approvedCount),
        formatNumber(r.rejectedByHotelCount),
        formatNumber(r.rejectedByClientCount),
        formatNumber(r.totalNights),
        formatNumber(r.totalGuests),
      ]);
      const csvContent = [headers, ...csvData]
        .map((row) => row.map((cell: unknown) => `"${String(cell)}"`).join(','))
        .join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `booking-statistics-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      alert(String(i18next.t('report-load-error')));
    } finally {
      setExportLoading(false);
    }
  };

  const handleNumberClick = (
    result: StatisticsReportOutput,
    columnType: 'totalRequests' | 'approvedCount' | 'rejectedByHotelCount' | 'rejectedByClientCount' | 'totalNights' | 'totalGuests',
    columnLabel: string
  ) => {
    setModalFilters({
      baseFilters: filters,
      requestYear: result.requestYear,
      requestMonth: result.requestMonth,
      hotelId: result.hotelId,
      offerSource: result.offerSource,
      eventYear: result.eventYear,
      eventMonth: result.eventMonth,
      columnType,
    });
    setModalColumnLabel(columnLabel);
    setModalOpen(true);
  };

  return (
    <Box component="form" sx={{ width: '100%' }}>
      {/* Filters */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, mb: 4 }}>
        {/* Date Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('statistics-request-date')}</Box>
          <FormControl>
            <SimpleDatePicker
              label={String(i18next.t('filter-from'))}
              value={filters.requestDateFrom ? new Date(filters.requestDateFrom) : null}
              onChange={(date) => handleFilterChange('requestDateFrom', date)}
            />
          </FormControl>
          <FormControl>
            <SimpleDatePicker
              label={String(i18next.t('filter-to'))}
              value={filters.requestDateTo ? new Date(filters.requestDateTo) : null}
              onChange={(date) => handleFilterChange('requestDateTo', date)}
            />
          </FormControl>
          <Box sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>{i18next.t('statistics-booking-date')}</Box>
          <FormControl>
            <SimpleDatePicker
              label={String(i18next.t('filter-from'))}
              value={filters.eventDateFrom ? new Date(filters.eventDateFrom) : null}
              onChange={(date) => handleFilterChange('eventDateFrom', date)}
            />
          </FormControl>
          <FormControl>
            <SimpleDatePicker
              label={String(i18next.t('filter-to'))}
              value={filters.eventDateTo ? new Date(filters.eventDateTo) : null}
              onChange={(date) => handleFilterChange('eventDateTo', date)}
            />
          </FormControl>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-offer-source')}</InputLabel>
            <Select
              label={String(i18next.t('filter-offer-source'))}
              value={filters.offerSource || ''}
              onChange={(e) => handleFilterChange('offerSource', (e.target.value as EOfferSource) || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value={EOfferSource.LISTER}>{i18next.t('enums-EOfferSource-LISTER')}</MenuItem>
              <MenuItem value={EOfferSource.WIDGET}>{i18next.t('enums-EOfferSource-WIDGET')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Hotel Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('reports-statistics-section-hotel')}</Box>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-country-hotel')}</InputLabel>
            <Select
              label={String(i18next.t('filter-country-hotel'))}
              value={filters.hotelCountry || ''}
              onChange={(e) => {
                handleFilterChange('hotelCountry', e.target.value || undefined);
                handleFilterChange('hotelState', undefined);
              }}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {allCountriesSorted.map(
                (country) =>
                  country && (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
          <FormControl disabled={!filters.hotelCountry}>
            <InputLabel shrink>{i18next.t('filter-state')}</InputLabel>
            <Select
              label={String(i18next.t('filter-state'))}
              value={filters.hotelState || ''}
              onChange={(e) => handleFilterChange('hotelState', e.target.value || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {hotelStates.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-hotel-group')}</InputLabel>
            <Select
              label={String(i18next.t('filter-hotel-group'))}
              multiple
              value={filters.hotelGroupIds || []}
              onChange={(e) => {
                const value = e.target.value as number[];
                handleFilterChange('hotelGroupIds', value.length > 0 ? value : undefined);
              }}
              input={<OutlinedInput label={String(i18next.t('filter-hotel-group'))} notched />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((value) => {
                    const group = hotelGroups.find((g) => g.id === value);
                    return <Chip key={value} label={group?.name || value} />;
                  })}
                </Box>
              )}
            >
              {hotelGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-agreement-type')}</InputLabel>
            <Select
              multiple
              label={String(i18next.t('filter-agreement-type'))}
              value={filters.hotelContractTypes || []}
              onChange={(e) => handleContractTypesChange(e.target.value as string[])}
              input={<OutlinedInput label={String(i18next.t('filter-agreement-type'))} notched />}
              displayEmpty
              renderValue={(selected) => {
                const values = selected as string[];
                if (values.length === 0) {
                  return '';
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {values.map((value) => (
                      <Chip key={value} label={contractTypeFilterOptions.find((o) => o.value === value)?.label || value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              <MenuItem value="ALL">
                <Checkbox checked={(filters.hotelContractTypes || []).includes('ALL')} />
                {i18next.t('filter-status-all')}
              </MenuItem>
              <MenuItem value="NONE">
                <Checkbox checked={(filters.hotelContractTypes || []).includes('NONE')} />
                {i18next.t('filter-agreement-no-contract')}
              </MenuItem>
              {getContractTypeOptions()
                .filter((option) => STATISTICS_RELEVANT_CONTRACT_TYPES.includes(option.value))
                .map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={(filters.hotelContractTypes || []).includes(option.value)} />
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={allHotels}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.name || '';
            }}
            value={null}
            inputValue={hotelSearchInput}
            onInputChange={(_, newInputValue) => {
              setHotelSearchInput(newInputValue);
              if (!newInputValue) {
                handleFilterChange('hotelName', undefined);
              }
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                // User typed something and pressed Enter (freeSolo)
                handleFilterChange('hotelName', newValue || undefined);
                setHotelSearchInput(newValue);
              } else if (newValue && typeof newValue === 'object') {
                // User selected a hotel from the list
                handleFilterChange('hotelName', newValue.name || undefined);
                setHotelSearchInput(newValue.name || '');
              } else {
                // User cleared the field
                handleFilterChange('hotelName', undefined);
                setHotelSearchInput('');
              }
            }}
            onBlur={() => {
              // When user leaves the field, use the input as hotelName filter
              if (hotelSearchInput) {
                handleFilterChange('hotelName', hotelSearchInput);
              }
            }}
            loading={hotelsLoading}
            renderInput={(params) => <TextField {...params} label={String(i18next.t('filter-hotel'))} variant="outlined" />}
          />
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-show-in-lister')}</InputLabel>
            <Select
              label={String(i18next.t('filter-show-in-lister'))}
              value={filters.showInLister === undefined ? '' : filters.showInLister ? 'yes' : 'no'}
              onChange={(e) =>
                handleFilterChange(
                  'showInLister',
                  e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined,
                )
              }
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value="yes">{i18next.t('filter-show-in-lister-yes')}</MenuItem>
              <MenuItem value="no">{i18next.t('filter-show-in-lister-no')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Client Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('reports-statistics-section-customer')}</Box>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-customer-country')}</InputLabel>
            <Select
              label={String(i18next.t('filter-customer-country'))}
              value={filters.clientCountry || ''}
              onChange={(e) => handleFilterChange('clientCountry', e.target.value || undefined)}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {allCountriesSorted.map(
                (country) =>
                  country && (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel shrink>{i18next.t('filter-customer-type')}</InputLabel>
            <Select
              label={String(i18next.t('filter-customer-type'))}
              value={filters.clientIsPremium === undefined ? '' : filters.clientIsPremium ? 'premium' : 'classic'}
              onChange={(e) =>
                handleFilterChange('clientIsPremium', e.target.value === 'premium' ? true : e.target.value === 'classic' ? false : undefined)
              }
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              <MenuItem value="classic">{i18next.t('filter-customer-type-classic')}</MenuItem>
              <MenuItem value="premium">{i18next.t('filter-customer-type-premium')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
        <Button variant="contained" color="primary" onClick={() => handleGenerateStatistics(0, pagination.rowsPerPage)} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : i18next.t('statistics-generate')}
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          {i18next.t('filter-reset')}
        </Button>
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{i18next.t('statistics-loading')}</Typography>
        </Box>
      )}

      {/* Results Table */}
      {!loading && results.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {i18next.t('statistics-results')} ({pagination.totalCount} {i18next.t('statistics-entries-found')})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={statisticsSort?.field === 'requestYear' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'requestYear'}
                      direction={statisticsSort?.field === 'requestYear' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('requestYear')}
                    >
                      {i18next.t('statistics-year')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={statisticsSort?.field === 'requestMonth' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'requestMonth'}
                      direction={statisticsSort?.field === 'requestMonth' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('requestMonth')}
                    >
                      {i18next.t('statistics-month')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={statisticsSort?.field === 'eventPeriod' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'eventPeriod'}
                      direction={statisticsSort?.field === 'eventPeriod' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('eventPeriod')}
                    >
                      {i18next.t('statistics-booking-month-range')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={statisticsSort?.field === 'hotelName' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'hotelName'}
                      direction={statisticsSort?.field === 'hotelName' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('hotelName')}
                    >
                      {i18next.t('statistics-hotel')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={statisticsSort?.field === 'offerSource' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'offerSource'}
                      direction={statisticsSort?.field === 'offerSource' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('offerSource')}
                    >
                      {i18next.t('statistics-source')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'totalRequests' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'totalRequests'}
                      direction={statisticsSort?.field === 'totalRequests' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('totalRequests')}
                    >
                      {i18next.t('statistics-requests')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'approvedCount' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'approvedCount'}
                      direction={statisticsSort?.field === 'approvedCount' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('approvedCount')}
                    >
                      {i18next.t('statistics-reservations')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'rejectedByHotelCount' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'rejectedByHotelCount'}
                      direction={statisticsSort?.field === 'rejectedByHotelCount' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('rejectedByHotelCount')}
                    >
                      {i18next.t('statistics-rejected-hotel')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'rejectedByClientCount' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'rejectedByClientCount'}
                      direction={statisticsSort?.field === 'rejectedByClientCount' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('rejectedByClientCount')}
                    >
                      {i18next.t('statistics-rejected-client')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'totalNights' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'totalNights'}
                      direction={statisticsSort?.field === 'totalNights' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('totalNights')}
                    >
                      {i18next.t('statistics-overnights')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={statisticsSort?.field === 'totalGuests' ? statisticsSort.direction : false}>
                    <TableSortLabel
                      active={statisticsSort?.field === 'totalGuests'}
                      direction={statisticsSort?.field === 'totalGuests' ? statisticsSort.direction : 'asc'}
                      onClick={() => handleStatisticsSort('totalGuests')}
                    >
                      {i18next.t('statistics-participants')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagination.page === 0 && allPagesTotals && (() => {
                  const fullList = allResultsRef.current || [];
                  const groupKeys = fullList.map((r) => ({
                    requestMonth: r.requestMonth,
                    hotelId: r.hotelId,
                    eventYear: r.eventYear,
                    eventMonth: r.eventMonth,
                  }));
                  const firstRow = fullList[0];
                  const gesamtFilters = (columnType: 'totalRequests' | 'approvedCount' | 'rejectedByHotelCount' | 'rejectedByClientCount' | 'totalNights' | 'totalGuests') => ({
                    baseFilters: filters,
                    requestYear: firstRow?.requestYear ?? 0,
                    requestMonth: 0,
                    hotelId: 0,
                    offerSource: undefined,
                    columnType,
                    groupKeys,
                  });
                  return (
                    <TableRow sx={{ backgroundColor: '#b0b0b0', fontWeight: 'bold' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('statistics-total-sum')}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('totalRequests') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-requests')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.totalRequests}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('approvedCount') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-reservations')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.approvedCount}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('rejectedByHotelCount') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-rejected-hotel')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.rejectedByHotelCount}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('rejectedByClientCount') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-rejected-client')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.rejectedByClientCount}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('totalNights') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-overnights')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.totalNights}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalFilters(gesamtFilters('totalGuests') as StatisticsReportBookingsFiltersInput);
                            setModalColumnLabel(`${i18next.t('statistics-participants')} - ${i18next.t('statistics-total-sum')}`);
                            setModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                        >
                          {allPagesTotals.totalGuests}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })()}
                {Object.entries(groupByYearAndHotel(sortedStatisticsRows))
                  .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                  .map(([year, hotelGroups]) => {
                    return (
                      <React.Fragment key={year}>
                        {Object.entries(hotelGroups).map(([hotelId, hotelData]) => {
                          const hotelTotal = calculateSubtotal(hotelData.results);
                          return (
                            <React.Fragment key={`${year}-${hotelId}`}>
                              {hotelData.results.map((result, index) => (
                                <TableRow
                                  key={`${year}-${hotelId}-${result.requestMonth}-${result.eventYear}-${result.eventMonth}-${result.offerSource}-${index}`}
                                >
                                  <TableCell>{result.requestYear}</TableCell>
                                  <TableCell>{getMonthName(result.requestMonth)}</TableCell>
                                  <TableCell>{formatStatisticsBookingMonthCell(result)}</TableCell>
                                  <TableCell>{result.hotelName}</TableCell>
                                  <TableCell>
                                    {result.offerSource === EOfferSource.LISTER
                                      ? i18next.t('enums-EOfferSource-LISTER')
                                      : i18next.t('enums-EOfferSource-WIDGET')}
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'totalRequests', String(i18next.t('statistics-requests')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.totalRequests}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'approvedCount', String(i18next.t('statistics-reservations')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.approvedCount}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'rejectedByHotelCount', String(i18next.t('statistics-rejected-hotel')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.rejectedByHotelCount}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'rejectedByClientCount', String(i18next.t('statistics-rejected-client')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.rejectedByClientCount}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'totalNights', String(i18next.t('statistics-overnights')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.totalNights}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNumberClick(result, 'totalGuests', String(i18next.t('statistics-participants')));
                                      }}
                                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                    >
                                      {result.totalGuests}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  {i18next.t('statistics-sum')} {hotelData.hotelName} {year}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'totalRequests',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-requests')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.totalRequests}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'approvedCount',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-reservations')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.approvedCount}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'rejectedByHotelCount',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-rejected-hotel')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.rejectedByHotelCount}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'rejectedByClientCount',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-rejected-client')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.rejectedByClientCount}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'totalNights',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-overnights')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.totalNights}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  <Box
                                    component="span"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const firstResult = hotelData.results[0];
                                      const hotelGroupKeys = hotelData.results.map((r) => ({
                                        requestMonth: r.requestMonth,
                                        hotelId: r.hotelId,
                                        eventYear: r.eventYear,
                                        eventMonth: r.eventMonth,
                                      }));
                                      setModalFilters({
                                        baseFilters: filters,
                                        requestYear: Number(year),
                                        requestMonth: 0,
                                        hotelId: firstResult.hotelId,
                                        offerSource: undefined,
                                        columnType: 'totalGuests',
                                        groupKeys: hotelGroupKeys,
                                      } as StatisticsReportBookingsFiltersInput);
                                      setModalColumnLabel(`${i18next.t('statistics-participants')} - ${i18next.t('statistics-sum')} ${hotelData.hotelName} ${year}`);
                                      setModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#AC2A6E', userSelect: 'none', display: 'block', textAlign: 'center' }}
                                  >
                                    {hotelTotal.totalGuests}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={pagination.page}
            onPageChange={(event, newPage) => handleChangePage(newPage)}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(event) => handleChangeRowsPerPage(parseInt(event.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100, 250]}
            {...getMuiTablePaginationI18nProps()}
          />
        </Box>
      )}

      {/* Export Button */}
      {!loading && results.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={exportLoading ? <CircularProgress size={20} /> : <GetAppIcon />}
            onClick={handleExportCSV}
            color="secondary"
            size="large"
            disabled={exportLoading}
          >
            {i18next.t('download-csv-report')}
          </Button>
        </Box>
      )}

      {/* Report detail modal */}
      {modalFilters && (
        <StatisticsBookingsModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalFilters(null);
          }}
          filters={modalFilters}
          columnLabel={modalColumnLabel}
          entityType={
            modalFilters.columnType === 'approvedCount' ||
              modalFilters.columnType === 'rejectedByHotelCount' ||
              modalFilters.columnType === 'rejectedByClientCount'
              ? 'booking'
              : 'offer'
          }
        />
      )}
    </Box>
  );
}
